/* ==========================================================================
   YCM OPTICALS — shared theme behaviour
   Small helpers the wishlist and the custom sections rely on. Anything Dawn
   already does (cart drawer, predictive search, variant picker) is left to
   Dawn — this file only fills gaps.
   ========================================================================== */
(function () {
  /* Money formatting that follows the shop's own currency format rather than
     hardcoding ₹ — the wishlist re-reads prices from /products/<handle>.js,
     which returns paise, not a formatted string. */
  var FMT = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
  window.ycmMoney = function (cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN',
        { style: 'currency', currency: FMT, minimumFractionDigits: 0 }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(0);
    }
  };

  /* Add to cart via the AJAX API, then tell Dawn so its drawer/bubble updates.
     Dawn listens for no event of its own, so we refetch the cart sections it
     renders — the same sections its own cart-drawer.js asks for. */
  window.ycmAddToCart = function (variantId, quantity) {
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] }),
    }).then(function (r) {
      if (!r.ok) throw new Error('add to cart failed');
      return r.json();
    }).then(function (data) {
      document.dispatchEvent(new CustomEvent('ycm:cart:added', { detail: data }));
      refreshCart(true);          // the shopper just clicked Add — show them the bag
      return data;
    });
  };

  /* ── a lens line cannot outlive its frame ────────────────────────────────
     Prescription lenses are an ADD-ON, not a product: they are ground to fit
     one frame and they carry that frame's prescription. Ordered on their own
     they are unfulfillable. The product page half of this guard is in
     sections/ycm-product.liquid (the add-on's own PDP offers no buy button);
     this is the other half -- the shopper who adds a pair and then removes the
     frame from the cart, which would otherwise leave a naked lens line behind.

     Both lines are stamped with the same `_ycm_pair` token when they are added
     (assets/ycm-lens.js). Underscore-prefixed, so Shopify keeps it on the order
     but never shows it. A lens line whose token no longer has a partner is an
     orphan and goes. Matching on the TOKEN rather than on "is there any frame
     in the cart" is what makes it right when there are two pairs and only one
     frame is removed.

     ⚠ Removal is by LINE KEY, never by index: /cart/change.js renumbers the
     lines after every call, so a second removal in the same pass would hit the
     wrong row. */
  /* -- give the shopper their bag back -------------------------------------
     "Buy it now" on a lens order has to replace the cart to reach a checkout
     holding only that order (see checkoutWithOnly in assets/ycm-lens.js), so it
     parks everything else here first. This runs on every storefront page load:
     if they completed the purchase the cart is empty and these are the items
     they had saved; if they abandoned it, the same. Either way they get their
     bag back without ever being told it went anywhere.

     ⚠️ Remove the key BEFORE the request, not after. A failed or slow add that
     leaves the key in place re-adds the same lines on the next page view, and
     the shopper watches their cart grow every time they click. */
  function restoreStashedBag() {
    var raw;
    /* There used to be a 'ycm:handoff' flag here that made this restore sit out
       one page load, because Buy it now navigated to /cart for Razorpay to open
       its modal there — a storefront page, so the parked bag would have been
       re-added before the modal opened. The hand-off now calls Razorpay's own
       openRzpCheckout() on the product page and navigates nowhere, so there is no
       page load to sit out and the flag has gone with it. */
    try { raw = localStorage.getItem('ycm:bag'); } catch (e) { return Promise.resolve(); }
    if (!raw) return Promise.resolve();
    try { localStorage.removeItem('ycm:bag'); } catch (e) {}
    var items;
    try { items = JSON.parse(raw); } catch (e) { return Promise.resolve(); }
    if (!items || !items.length) return Promise.resolve();
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function () { refreshCart(false); }).catch(function () {});
  }

  /* ── does the icon agree with the cart? ──────────────────────────────────
     Client, twice: "the cart icon on header still sometimes updates to the old
     one and it happens still". The bfcache guard at the bottom of this file
     catches a back-navigation, but it cannot catch a page whose HTML came from
     a cache with yesterday's count baked into it — no script re-runs there
     either, and `pageshow` reports persisted:false.

     Rather than add a request, reconcile inside the /cart.js call the orphan
     check already makes on every page load: read the number the bubble is
     showing, compare, and re-render only when they disagree. Costs nothing when
     the page is correct, which is almost always. */
  function bubbleCount() {
    var b = document.getElementById('cart-icon-bubble');
    if (!b) return null;
    var bubble = b.querySelector('.cart-count-bubble');
    if (!bubble) return 0;              // no bubble at all is Dawn's zero
    /* ⚠️ cart-icon-bubble.liquid renders the NUMBER only `if cart.item_count <
       100`. Above that the bubble exists with no digits in it, so reading the
       span and defaulting to 0 would report a mismatch on every page load and
       refetch the sections forever. Unknown means unknown: skip the check. */
    var n = bubble.querySelector('span[aria-hidden="true"]');
    if (!n) return null;
    var v = parseInt(n.textContent.trim(), 10);
    return isNaN(v) ? null : v;
  }

  function pruneOrphanLenses() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return false;
        var shown = bubbleCount();
        if (shown !== null && shown !== cart.item_count) refreshCart(false);
        if (!cart.items || !cart.items.length) return false;
        var partners = {};
        cart.items.forEach(function (i) {
          var t = i.properties && i.properties._ycm_pair;
          // "Lens" is written only onto the lens line, so it names the add-on
          // without this file needing to know the add-on's handle.
          if (t && !(i.properties && i.properties.Lens)) partners[t] = true;
        });
        var orphans = cart.items.filter(function (i) {
          var pr = i.properties || {};
          return pr._ycm_pair && pr.Lens && !partners[pr._ycm_pair];
        });
        if (!orphans.length) return false;
        return orphans.reduce(function (chain, i) {
          return chain.then(function () {
            return fetch(root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: i.key, quantity: 0 })
            });
          });
        }, Promise.resolve()).then(function () { return true; });
      })
      .catch(function () { return false; });
  }

  // Dawn re-renders the cart sections on every line change and fires no event
  // of its own, so watch the count bubble instead: it is the one node Dawn
  // always rewrites, on the cart page and in the drawer alike.
  function watchCart() {
    var run = false;
    var check = function () {
      if (run) return;
      run = true;
      pruneOrphanLenses().then(function (pruned) {
        run = false;
        if (pruned) refreshCart(false);
      });
    };
    restoreStashedBag().then(check);
    var host = document.querySelector('cart-drawer, cart-items, #main-cart-items');
    if (!host) return;
    new MutationObserver(function () { check(); })
      .observe(host, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCart);
  } else watchCart();

  /* ⛔ `open` IS NOT OPTIONAL POLISH — IT IS THE BUG THE CLIENT REPORTED.
     This function used to end with an unconditional `drawer.open()`, so
     "re-render the cart" and "slide the cart out over the page" were the same
     action. Three callers wanted only the first:

       · restoreStashedBag(), which runs on EVERY storefront page load that has
         a parked bag — i.e. after every lens "Buy it now". So the shopper came
         back from the payment page, or simply refreshed, and the cart drawer
         opened by itself over whatever they were reading. Client, 2026-08-31:
         "when i do a buy now on eyeglasses and come back from payment page ...
         the cart slider pops up", and "when i come back from checkout and go to
         any other page or refresh, the cart opens again".
       · the orphan-lens prune, which is housekeeping nobody asked to see.
       · the bfcache refresh below.

     Only a real Add to cart passes true. Defaulting to false is deliberate: a
     future caller that forgets the argument gets the quiet behaviour, and a
     drawer that fails to open is a far smaller bug than one that opens itself. */
  function refreshCart(open) {
    var drawer = document.querySelector('cart-drawer');
    var bubble = document.getElementById('cart-icon-bubble');
    var ids = [];
    if (drawer) ids.push('cart-drawer');
    if (bubble) ids.push('cart-icon-bubble');
    if (!ids.length) return;
    /* ⚠️ THE BUBBLE LOOKUP USED TO THROW AND TAKE THE DRAWER DOWN WITH IT.
       sections/cart-icon-bubble.liquid renders ONLY the inner markup — the two
       icon spans and the count bubble. The id="cart-icon-bubble" lives on the
       <a> in sections/header.liquid, which is NOT part of that section. So
       `.querySelector('#cart-icon-bubble')` returned null, `.innerHTML` on null
       threw a TypeError, the promise rejected, and the DRAWER UPDATE BELOW IT
       NEVER RAN — with no .catch, entirely silently.

       That is why a shopper who added a prescription frame was shown "Your cart
       is empty" over a cart that was completely correct: the data was always
       right, only the re-render was dead. Verified against the live store —
       Shopify's own section render returns the drawer with both lines in it and
       the bubble reading "2 items".

       The response IS the element's inner HTML, so take it whole and only fall
       back to digging for a wrapper. And never let one section's markup moving
       silently kill the other's update again: resolve each independently and
       catch at the end. */
    /* ⚠️ TWO MORE WAYS TO SHOW "the old one", both of which look identical to
       the bfcache case and neither of which the code above could survive:

       · a cached RESPONSE. This is a plain GET, so a back-navigation can be
         answered from the HTTP cache with the count as it was before the order.
         no-store settles it.
       · a RACE. Three call sites can have refreshes in flight at once (the
         restore, the prune, a real add), and there is no promise ordering
         guarantee — an older response landing last repaints the older count.
         Stamp each call and let only the newest write. */
    var seq = ++refreshCart._seq;
    fetch(window.Shopify.routes.root + '?sections=' + ids.join(','), { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        if (seq !== refreshCart._seq) return;      // a newer refresh already won
        if (sections['cart-icon-bubble'] && bubble) {
          var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var binner = bdoc.querySelector('#cart-icon-bubble')
                    || bdoc.querySelector('#shopify-section-cart-icon-bubble');
          bubble.innerHTML = binner ? binner.innerHTML : bdoc.body.innerHTML;
        }
        if (sections['cart-drawer'] && drawer) {
          var fresh = new DOMParser()
            .parseFromString(sections['cart-drawer'], 'text/html')
            .querySelector('cart-drawer');
          if (fresh) {
            drawer.innerHTML = fresh.innerHTML;
            // Mirror the real state, both ways: this also has to put .is-empty
            // BACK when the last line is removed.
            drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          }
          if (open && typeof drawer.open === 'function') drawer.open();
        }
      })
      .catch(function () { /* a stale drawer is bad; a dead promise chain is worse */ });
  }
  refreshCart._seq = 0;

  /* ⚠️ EXPOSED ON PURPOSE. assets/ycm-lens.js adds the frame and the lens with
     its own /cart/add.js call (two line items with properties — Dawn's product
     form cannot express that), so Dawn never re-renders the drawer for it and
     nothing here would either.

     It used to reach for `window.ycmAddToCart.refresh`, which NEVER EXISTED:
     ycmAddToCart is a FUNCTION, and a function has no `.refresh` property, so
     the guard was silently false every single time. The drawer then opened
     still holding its page-load markup — "Your cart is empty" — on a cart that
     had just been filled correctly. No error, nothing in the console, and the
     cart icon reverted as soon as anything re-rendered it. Every prescription
     frame on the store did this, in both buy modes.

     A guard on a property that is never set is indistinguishable from a feature
     that is switched off. Export the function and call it by name. */
  /* ycm-lens.js calls this with no argument after its own two-line
     /cart/add.js and DOES want the drawer — that is a real Add to cart. So the
     exported form defaults the other way from the internal one, and says so. */
  window.ycmRefreshCart = function (open) { refreshCart(open !== false); };

  /* ── the stale cart icon ─────────────────────────────────────────────────
     Client: "the cart icon on header still sometimes updates to the old one".
     Coming BACK from the checkout is a back-navigation, and the browser serves
     that from the bfcache: the whole page is restored exactly as it was left,
     no script re-runs, no DOMContentLoaded — so the bubble still shows the
     count from before the order. Nothing is broken and nothing will fix it on
     its own, because as far as the page is concerned no time has passed.

     `pageshow` with persisted=true is the one event that fires for a bfcache
     restore. Re-render quietly (never open the drawer — that was the other half
     of the report). */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refreshCart(false);
  });
})();

/* Rail carousels with edge arrows that auto-hide at the start and the end.
   Ported from NSE's nse.js; ycm-brand-row is the only caller so far.
   Exposed as YCMreinit so async-injected cards (the recommendations row) can
   bind their arrows after the fetch lands. */
(function () {
  function carousels() {
    document.querySelectorAll('[data-ycm-carousel]').forEach(function (w) {
      if (w.__c) return; w.__c = true;
      var track = w.querySelector('[data-carousel-track]'); if (!track) return;
      var prev = w.querySelector('[data-carousel-prev]');
      var next = w.querySelector('[data-carousel-next]');
      function upd() {
        var max = track.scrollWidth - track.clientWidth - 2, x = track.scrollLeft;
        if (prev) prev.classList.toggle('is-off', x <= 2);
        if (next) next.classList.toggle('is-off', max <= 2 || x >= max);
      }
      function nudge(dir) { track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' }); }
      if (prev) prev.addEventListener('click', function () { nudge(-1); });
      if (next) next.addEventListener('click', function () { nudge(1); });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(upd); }, { passive: true });
      window.addEventListener('resize', upd);
      upd();
    });
  }
  window.YCMreinit = carousels;
  if (document.readyState !== 'loading') carousels();
  else document.addEventListener('DOMContentLoaded', carousels);
})();

/* Lazy videos (ported from NSE): the card ships preload="none" with the real
   URL in data-src, so eight 9:16 clips do not download until they are near the
   screen. Eight autoplaying videos preloading at once is megabytes on a phone.

   ⚠️ rootMargin was 200px, which is about a fifth of a screen — the fetch
   started so late that the shopper watched the card sit empty while it
   downloaded ("even that loads too late"). 800px starts it roughly one screen
   early, which is still lazy (nothing below the second row is touched on
   arrival) but is far enough ahead to be over before the card is looked at.
   The poster in snippets/ycm-video-card.liquid covers whatever gap is left, so
   the card is never a blank navy box either way.

   ⛔ THE CLIENT REPORTED THESE DEAD ON AN IPHONE, AND THIS IS WHY.
   The markup had no `autoplay` attribute, so playback existed only because
   this file called .play() — ONCE, at rootMargin 800px, i.e. while the card
   was still a full screen below the fold — and then unobserved it, so there
   was never a second attempt. Desktop Chrome happily keeps an off-screen video
   playing, which is why it looked fine everywhere we tested. WebKit does not:
   iOS pauses media that is not in the viewport to save power, and only
   auto-resumes the elements it is MANAGING, which means the ones carrying the
   autoplay attribute. A scripted play() on an attribute-less element therefore
   started, got paused off-screen, and stayed paused for ever — showing the
   poster, silently, because the rejection was swallowed by an empty .catch().

   So: `autoplay` is on the element now and the browser owns the pause/resume
   lifecycle. The loading stays lazy — with every source on data-src there is
   no valid source to select, so autoplay has nothing to fetch until load()
   runs (verified by counting network requests, not by reading the spec). */
(function () {
  var vids = document.querySelectorAll('video[data-ycm-lazyvideo]');
  if (!vids.length) return;

  var load = function (v) {
    if (v.dataset.ycmLoaded) return;
    v.dataset.ycmLoaded = '1';
    v.querySelectorAll('source[data-src]').forEach(function (s) { s.src = s.dataset.src; });
    v.load();          // the autoplay attribute takes over from here
  };

  /* Re-ask, rather than assuming the first ask stuck. play() on an element that
     is already playing resolves immediately, so this is free when nothing is
     wrong. A rejection is REPORTED, not swallowed: Low Power Mode is a genuine
     refusal and a silent one is indistinguishable from a bug in this file. */
  var play = function (v) {
    if (!v.paused) return;
    var p = v.play();
    if (p && p.catch) p.catch(function (e) {
      if (window.console) console.warn('[ycm] video autoplay refused:', e && e.name);
    });
  };

  if (!('IntersectionObserver' in window)) {
    vids.forEach(function (v) { load(v); play(v); });
    return;
  }

  /* Two observers, one job each. Loading happens a screen early so the clip is
     ready before it is looked at; PLAYING is asked for only when the card is
     really on screen, and is never unobserved. */
  var loadIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { load(e.target); loadIO.unobserve(e.target); }
    });
  }, { rootMargin: '800px' });

  var playIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { load(e.target); play(e.target); } });
  });

  /* ⚠️ DO NOT GATE THIS ON `window.load`. Tried, measured, reverted: this page
     keeps requests pending (Razorpay, the clips themselves), so `load` did not
     fire until ~5.6s and every card sat on its poster until then — which is the
     "even that loads too late" complaint all over again. The observers start
     immediately; whether that costs the hero anything is measured separately. */
  vids.forEach(function (v) { loadIO.observe(v); playIO.observe(v); });
})();
